import { useQuery } from 'react-query'
import { apiService } from '../services/api'
import type { Team, TeamMember } from '../types'

interface MyTeamResponse {
  teamFeatureEnabled: boolean
  assigned?: boolean
  team?: Team
  members?: TeamMember[]
}

interface UseTeamFeatureReturn {
  /** Whether the team feature is enabled AND loaded. False while loading or when disabled. */
  enabled: boolean
  /** Whether the query is still in-flight. */
  loading: boolean
  /** Whether the pilot has been assigned to a team. False when not assigned or feature disabled. */
  assigned: boolean
  /** The pilot's team, or null when not assigned or feature disabled. */
  team: Team | null
  /** The team members list. Empty when not assigned or feature disabled. */
  members: TeamMember[]
  /** Re-fetch team data from the server. */
  refetch: () => void
}

export function useTeamFeature(): UseTeamFeatureReturn {
  const { data, isLoading, refetch } = useQuery<MyTeamResponse>(
    'myTeam',
    async () => {
      const response = await apiService.getMyTeam()
      if (response.error) {
        throw new Error(response.error.message)
      }
      // Handle both wrapped ({ data: { ... } }) and unwrapped response formats
      const responseData = (response.data as any)?.data || response.data
      return responseData as MyTeamResponse
    },
    {
      retry: 1,
      // Treat errors gracefully — component will see enabled: false
      useErrorBoundary: false,
    }
  )

  // While loading or on error, treat as disabled
  if (isLoading || !data) {
    return {
      enabled: false,
      loading: isLoading,
      assigned: false,
      team: null,
      members: [],
      refetch,
    }
  }

  // Feature flag is disabled on the server
  if (!data.teamFeatureEnabled) {
    return {
      enabled: false,
      loading: false,
      assigned: false,
      team: null,
      members: [],
      refetch,
    }
  }

  // Feature enabled but pilot not assigned
  if (!data.assigned) {
    return {
      enabled: true,
      loading: false,
      assigned: false,
      team: null,
      members: [],
      refetch,
    }
  }

  // Feature enabled and pilot is assigned
  return {
    enabled: true,
    loading: false,
    assigned: true,
    team: data.team ?? null,
    members: data.members ?? [],
    refetch,
  }
}
